import { UserIdentity } from '@backstage/core-components';
import { type IdentityApi, useApi } from '@backstage/core-plugin-api';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cognitoAuthApiRef } from './appModuleOidc';
import { localAuthApiRef } from './localAuthApi';
import './RdapSignInPage.css';

type RdapSignInPageProps = {
  onSignInSuccess: (identity: IdentityApi) => void;
};

const AwsLogo = () => (
  <svg
    aria-hidden="true"
    className="rdap-aws-logo"
    viewBox="-3 -3 68 30"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.2 4.5 4.8 16.1H1.3L6.2 1.7h4l4.9 14.4h-3.5L8.2 4.5Zm-4.1 8.8h8.2v2.8H4.1v-2.8Zm14.5 2.8L15.1 1.7h3.5l2.1 10.2 2.5-10.2h3l2.5 10.2 2.1-10.2h3.5l-3.5 14.4h-3.6L24.6 6l-2.6 10.1h-3.4Zm17.1-4.3h3.4c.1 1.3 1.2 2.1 3.1 2.1 1.8 0 2.9-.7 2.9-1.8 0-.9-.7-1.4-2.3-1.7l-2.1-.4c-3.1-.6-4.6-2-4.6-4.4 0-2.8 2.4-4.6 5.9-4.6 3.7 0 5.9 1.8 6 4.5h-3.3c-.1-1.2-1.1-1.9-2.7-1.9-1.6 0-2.6.7-2.6 1.7 0 .8.7 1.3 2.2 1.6l1.9.4c3.3.7 4.8 2 4.8 4.4 0 3-2.4 4.8-6.3 4.8-3.8 0-6.1-1.8-6.3-4.7Z"
      fill="currentColor"
    />
    <path
      d="M37.1 19.2c6.9 3.2 15.3 2.3 21.1-2.2.4-.3.1-.7-.3-.5-6.1 3.4-13.6 4.1-20.3 1.6-.5-.2-.8.8-.5 1.1Z"
      fill="#ff9900"
    />
    <path
      d="M54.1 16.2c1.1-.1 3.7-.3 4.1.2.4.4-.1 2.9-.3 3.9-.1.3.2.4.4.2 1.4-1.5 1.8-4.7 1.5-5.1-.4-.4-3.5-1-5.1.2-.2.2-.1.5.1.4.2-.1.6-.2 1.3-.3Z"
      fill="#ff9900"
    />
  </svg>
);

export function RdapSignInPage({ onSignInSuccess }: RdapSignInPageProps) {
  const authApi = useApi(cognitoAuthApiRef);
  const localAuthApi = useApi(localAuthApiRef);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<Error>();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const completeSignIn = useCallback(
    async (instantPopup: boolean) => {
      setError(undefined);
      setSigningIn(instantPopup);

      try {
        const identityResponse = await authApi.getBackstageIdentity(
          instantPopup ? { instantPopup: true } : { optional: true },
        );

        if (!identityResponse) {
          return;
        }

        const profile = await authApi.getProfile();
        onSignInSuccess(
          UserIdentity.create({
            identity: identityResponse.identity,
            authApi,
            profile,
          }),
        );
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError)),
        );
      } finally {
        setLoading(false);
        setSigningIn(false);
      }
    },
    [authApi, onSignInSuccess],
  );

  const signInLocally = useCallback(async () => {
    setError(undefined);
    setSigningIn(true);

    try {
      const identity = await localAuthApi.localSignIn(username, password);
      const profile = await localAuthApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identity.identity,
          authApi: localAuthApi,
          profile,
        }),
      );
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError
          : new Error(String(caughtError)),
      );
    } finally {
      setSigningIn(false);
    }
  }, [localAuthApi, onSignInSuccess, password, username]);

  useEffect(() => {
    const queryError = searchParams.get('error');
    if (queryError) {
      setError(new Error(queryError));
      setLoading(false);
      return;
    }

    void completeSignIn(false);
  }, [completeSignIn, searchParams]);

  if (loading) {
    return (
      <main className="rdap-sign-in-page rdap-sign-in-loading">
        <CircularProgress aria-label="Loading sign-in" />
      </main>
    );
  }

  return (
    <main className="rdap-sign-in-page">
      <section
        className="rdap-sign-in-card"
        aria-labelledby="rdap-sign-in-title"
      >
        <img
          className="rdap-sign-in-brand"
          src="/chatbot-sense.png"
          alt="Chatbot R-DAP"
        />
        <div className="rdap-sign-in-divider" />
        <h1 id="rdap-sign-in-title">Welcome to R-DAP</h1>
        <p>Access the platform with your corporate account.</p>

        {error ? (
          <div className="rdap-sign-in-error" role="alert">
            <strong>Unable to sign in</strong>
            <span>{error.message || 'Please try again.'}</span>
          </div>
        ) : null}

        <form
          className="rdap-local-sign-in"
          onSubmit={event => {
            event.preventDefault();
            void signInLocally();
          }}
        >
          <input
            autoComplete="username"
            disabled={signingIn}
            onChange={event => setUsername(event.target.value)}
            placeholder="Username"
            required
            value={username}
          />
          <input
            autoComplete="current-password"
            disabled={signingIn}
            onChange={event => setPassword(event.target.value)}
            placeholder="Password"
            required
            type="password"
            value={password}
          />
          <button
            className="rdap-local-button"
            disabled={signingIn}
            type="submit"
          >
            {signingIn ? <CircularProgress size={20} /> : 'Login locally'}
          </button>
        </form>

        <div className="rdap-auth-separator">
          <span>or</span>
        </div>

        <button
          className="rdap-cognito-button"
          type="button"
          disabled={signingIn}
          onClick={() => void completeSignIn(true)}
        >
          {signingIn ? <CircularProgress size={20} /> : <AwsLogo />}
          <span>{signingIn ? 'Connecting…' : 'Login with AWS Cognito'}</span>
        </button>
      </section>
    </main>
  );
}
